import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Box, { BoxProps } from '@mui/material/Box'
import MuiBreadcrumbs from '@mui/material/Breadcrumbs'
import ListItemIcon from '@mui/material/ListItemIcon'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { makeStyles } from 'tss-react/mui'

const useStyles = makeStyles()((theme) => ({
  link: {
    textDecoration: 'none',
    color: 'inherit',
  },
  breadcrumb: {
    display: 'flex',
    color: 'inherit',
    marginRight: theme.spacing(1),
  },
  menuBreadcrumb: {
    color: 'inherit',
    cursor: 'pointer',
  },
  icon: {
    marginRight: theme.spacing(1),
  },
  dropdownIcon: {
    marginLeft: theme.spacing(2),
  },
}))

export const Breadcrumbs: React.FC<React.PropsWithChildren<BoxProps>> = ({
  children,
  ...props
}) => {
  return (
    <Box {...props}>
      <MuiBreadcrumbs separator={<ChevronRightIcon />}>
        {children}
      </MuiBreadcrumbs>
    </Box>
  )
}

export interface BreadcrumbProps {
  to?: string | null
  Icon?: React.ComponentType<React.PropsWithChildren<{ className?: string }>>
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
}

export const Breadcrumb: React.FC<React.PropsWithChildren<BreadcrumbProps>> = ({
  Icon,
  children,
  to,
  onClick = () => {},
}) => {
  const { classes } = useStyles()
  const breadcrumbChild = (
    <div className={classes.breadcrumb} onClick={onClick}>
      {Icon && <Icon className={classes.icon} />}
      {children}
    </div>
  )
  return !to ? (
    // If there is nothing to link to (aka after a search or selected tags), we don't want the "Results" icon to link to anything
    breadcrumbChild
  ) : (
    // In any other case, we want the breadcrumb to be a Link
    <Link className={classes.link} to={to}>
      {breadcrumbChild}
    </Link>
  )
}

export interface BreadcrumbMenuProps {
  selected: string
  items: {
    Icon: React.ComponentType<React.PropsWithChildren<{ className?: string }>>
    label: string
    key: string
    to: string
  }[]
}

export const BreadcrumbMenu: React.FC<
  React.PropsWithChildren<BreadcrumbMenuProps>
> = ({ items = [], selected }) => {
  const { classes, cx } = useStyles()
  const [anchorEl, setAnchorEl] = useState<Element | null>(null)

  const selectedItem = items.find((item) => item.key === selected) || items[0]
  const handleClick = (event: React.MouseEvent) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  if (!selectedItem) {
    return null
  }

  return (
    <div>
      <div
        className={cx(classes.menuBreadcrumb, classes.breadcrumb)}
        onClick={handleClick}
      >
        <selectedItem.Icon className={classes.icon} />
        {selectedItem.label}
        {anchorEl ? (
          <ExpandLess className={classes.dropdownIcon} />
        ) : (
          <ExpandMore className={classes.dropdownIcon} />
        )}
      </div>
      <Menu anchorEl={anchorEl} onClose={handleClose} open={Boolean(anchorEl)}>
        {items.map((item) => (
          <MenuItem
            component={Link}
            onClick={handleClose}
            selected={selected === item.key}
            to={item.to}
            key={`breadcrumb-${item.key}`}
          >
            <ListItemIcon>
              <item.Icon />
            </ListItemIcon>
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  )
}
